// Centralized image handling utility for robust image display
// Provides fallback images by category and handles broken images

// High-quality external images by category (Pexels CDN - reliable and fast)
export const CATEGORY_IMAGES = {
  Books: [
    'https://images.pexels.com/photos/1907785/pexels-photo-1907785.jpeg?auto=compress&cs=tinysrgb&w=600',
    'https://images.pexels.com/photos/733852/pexels-photo-733852.jpeg?auto=compress&cs=tinysrgb&w=600',
    'https://images.pexels.com/photos/256450/pexels-photo-256450.jpeg?auto=compress&cs=tinysrgb&w=600',
    'https://images.pexels.com/photos/159711/books-bookstore-book-reading-159711.jpeg?auto=compress&cs=tinysrgb&w=600',
    'https://images.pexels.com/photos/1148399/pexels-photo-1148399.jpeg?auto=compress&cs=tinysrgb&w=600',
    'https://images.pexels.com/photos/904616/pexels-photo-904616.jpeg?auto=compress&cs=tinysrgb&w=600',
  ],
  Crystals: [
    'https://images.pexels.com/photos/6766258/pexels-photo-6766258.jpeg?auto=compress&cs=tinysrgb&w=600',
    'https://images.pexels.com/photos/5737625/pexels-photo-5737625.jpeg?auto=compress&cs=tinysrgb&w=600',
    'https://images.pexels.com/photos/1573241/pexels-photo-1573241.jpeg?auto=compress&cs=tinysrgb&w=600',
    'https://images.pexels.com/photos/2942855/pexels-photo-2942855.jpeg?auto=compress&cs=tinysrgb&w=600',
  ],
  Incense: [
    'https://images.pexels.com/photos/4207892/pexels-photo-4207892.jpeg?auto=compress&cs=tinysrgb&w=600',
    'https://images.pexels.com/photos/4207893/pexels-photo-4207893.jpeg?auto=compress&cs=tinysrgb&w=600',
    'https://images.pexels.com/photos/4207890/pexels-photo-4207890.jpeg?auto=compress&cs=tinysrgb&w=600',
    'https://images.pexels.com/photos/3059892/pexels-photo-3059892.jpeg?auto=compress&cs=tinysrgb&w=600',
  ],
  Soaps: [
    'https://images.pexels.com/photos/3737600/pexels-photo-3737600.jpeg?auto=compress&cs=tinysrgb&w=600',
    'https://images.pexels.com/photos/3737585/pexels-photo-3737585.jpeg?auto=compress&cs=tinysrgb&w=600',
    'https://images.pexels.com/photos/3737593/pexels-photo-3737593.jpeg?auto=compress&cs=tinysrgb&w=600',
    'https://images.pexels.com/photos/4041392/pexels-photo-4041392.jpeg?auto=compress&cs=tinysrgb&w=600',
  ],
  Toys: [
    'https://images.pexels.com/photos/3661193/pexels-photo-3661193.jpeg?auto=compress&cs=tinysrgb&w=600',
    'https://images.pexels.com/photos/4226902/pexels-photo-4226902.jpeg?auto=compress&cs=tinysrgb&w=600',
    'https://images.pexels.com/photos/3661190/pexels-photo-3661190.jpeg?auto=compress&cs=tinysrgb&w=600',
    'https://images.pexels.com/photos/163036/mario-luigi-yoschi-figures-163036.jpeg?auto=compress&cs=tinysrgb&w=600',
  ],
  Miscellaneous: [
    'https://images.pexels.com/photos/3270223/pexels-photo-3270223.jpeg?auto=compress&cs=tinysrgb&w=600',
    'https://images.pexels.com/photos/6766247/pexels-photo-6766247.jpeg?auto=compress&cs=tinysrgb&w=600',
    'https://images.pexels.com/photos/5632399/pexels-photo-5632399.jpeg?auto=compress&cs=tinysrgb&w=600',
    'https://images.pexels.com/photos/5632381/pexels-photo-5632381.jpeg?auto=compress&cs=tinysrgb&w=600',
  ],
};

// Default fallback image (books - matches store theme)
export const DEFAULT_IMAGE = 'https://images.pexels.com/photos/256450/pexels-photo-256450.jpeg?auto=compress&cs=tinysrgb&w=600';

// Get a fallback image for a specific category
export const getCategoryImage = (categoryName, index = 0) => {
  const images = CATEGORY_IMAGES[categoryName] || CATEGORY_IMAGES.Miscellaneous;
  return images[index % images.length];
};

// Get a random image for a category
export const getRandomCategoryImage = (categoryName) => {
  const images = CATEGORY_IMAGES[categoryName] || CATEGORY_IMAGES.Miscellaneous;
  const randomIndex = Math.floor(Math.random() * images.length);
  return images[randomIndex];
};

// Check if a URL is a local upload (which won't work in production)
export const isLocalUpload = (url) => {
  if (!url) return false;
  return url.includes('/api/uploads/') || url.includes('localhost') || url.includes('127.0.0.1');
};

// Get a valid image URL - returns fallback if the URL is local/invalid
export const getValidImageUrl = (imageUrl, categoryName = 'Miscellaneous', productId = '') => {
  // If no URL or it's a local upload, use category fallback
  if (!imageUrl || isLocalUpload(imageUrl)) {
    // Use product ID to get consistent image for same product
    const index = productId ? productId.charCodeAt(0) % CATEGORY_IMAGES[categoryName]?.length || 0 : 0;
    return getCategoryImage(categoryName, index);
  }
  return imageUrl;
};

// Validate if an image URL is accessible (for admin preview)
export const validateImageUrl = async (url) => {
  if (!url) return { valid: false, error: 'No URL provided' };
  if (isLocalUpload(url)) return { valid: false, error: 'Local uploads not supported in production' };
  
  try {
    const response = await fetch(url, { method: 'HEAD', mode: 'no-cors' });
    return { valid: true };
  } catch (error) {
    return { valid: false, error: 'Image URL not accessible' };
  }
};

// Preload an image and return a promise
export const preloadImage = (url) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(url);
    img.onerror = () => reject(new Error(`Failed to load image: ${url}`));
    img.src = url;
  });
};
